import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/pages/admin/AdminShell.jsx";

export default function Team() {
  const [employees, setEmployees] = useState([]);
  const [teams, setTeams] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [modules, setModules] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [exiting, setExiting] = useState(null);
  const [reviewing, setReviewing] = useState(null);

  const endEngagement = async () => {
    if (!exiting.exit_date) return toast.warning("Pick an exit date");
    await api.post(`/employees/${exiting.employee_id}/end-engagement`, {
      exit_date: exiting.exit_date, reason: exiting.reason, notes: exiting.notes, create_offboarding: true,
    });
    toast.success(`${exiting.display_name || exiting.legal_name} marked exited · offboarding checklist created`);
    setExiting(null); load();
  };

  const saveReview = async () => {
    if (!reviewing.rating) return toast.warning("Rating required");
    await api.post("/reviews", {
      employee_id: reviewing.employee_id, period: reviewing.period, rating: reviewing.rating,
      strengths: reviewing.strengths, improvements: reviewing.improvements, goals_next: reviewing.goals_next,
      visible_to_employee: !!reviewing.visible_to_employee,
    });
    toast.success("Review saved"); setReviewing(null);
  };

  const load = async () => {
    const [e, t, a, m] = await Promise.all([api.get("/employees"), api.get("/teams"), api.get("/training/assignments"), api.get("/training/modules")]);
    setEmployees(e.data); setTeams(t.data); setAssignments(a.data); setModules(m.data);
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    return employees.map((e) => {
      const mine = assignments.filter((a) => a.employee_id === e.employee_id);
      const required = mine.filter((a) => (modules.find((m) => m.module_id === a.module_id) || {}).required);
      const passed = required.filter((a) => a.status === "passed").length;
      const pct = required.length ? Math.round(passed / required.length * 100) : 0;
      return { ...e, training_pct: pct };
    }).filter((e) => {
      if (filter === "active" && e.status !== "active") return false;
      if (filter === "onboarding" && e.status !== "pre_joining") return false;
      if (filter === "exited" && e.status !== "exited") return false;
      if (search && !((e.display_name || e.legal_name || "").toLowerCase().includes(search.toLowerCase()) || (e.email || "").toLowerCase().includes(search.toLowerCase()))) return false;
      return true;
    });
  }, [employees, assignments, modules, filter, search]);

  const stats = [
    { label: "Headcount", value: employees.filter((e) => e.status !== "exited").length },
    { label: "Onboarding", value: employees.filter((e) => e.status === "pre_joining").length, accent: "warn" },
    { label: "Ready", value: employees.filter((e) => e.ready_at).length, accent: "pass" },
    { label: "Teams", value: teams.length },
  ];

  const exportCsv = () => {
    const headers = ["employee_number","name","email","role","team","joining_date","tenure_days","training_pct","status"];
    const esc = (v) => JSON.stringify(String(v == null ? "" : v));
    const lines = [headers.join(",")];
    rows.forEach((e) => {
      lines.push([
        e.employee_number || "", e.display_name || e.legal_name || "", e.email || "", e.role_title || "", e.team || "",
        e.joining_date || "", e.tenure_days || 0, e.training_pct, e.ready_at ? "ready" : e.status,
      ].map(esc).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "team.csv"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported");
  };

  return (
    <div>
      <PageHeader
        eyebrow="PEOPLE · TEAM"
        title="Team"
        subtitle={`${rows.length} shown · auto-numbered TT-NNNN`}
        stats={stats}
        action={<button onClick={exportCsv} className="tt-btn-secondary text-sm">Export CSV</button>}
      />

      <div className="flex items-center gap-2 mb-4">
        <input className="tt-input !w-72" placeholder="Search name or email…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="team-search" />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="tt-input !w-auto text-sm">
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="onboarding">Onboarding</option>
          <option value="exited">Exited</option>
        </select>
      </div>

      <div className="tt-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0F0F0F]">
            <tr className="text-[#71717A] text-[10px] font-mono tracking-wider uppercase">
              <th className="text-left px-4 py-2">ID</th>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Role</th>
              <th className="text-left px-4 py-2">Team</th>
              <th className="text-left px-4 py-2">Joined</th>
              <th className="text-right px-4 py-2">Tenure</th>
              <th className="text-right px-4 py-2">Training</th>
              <th className="text-left px-4 py-2 pl-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.employee_id} className="border-t border-[#1E1E1E] hover:bg-[#121212] cursor-pointer" onClick={() => (window.location.href = `/admin/team/${e.employee_id}`)} data-testid={`team-row-${e.employee_id}`}>
                <td className="px-4 py-3 font-mono text-xs text-[#A1A1AA]">{e.employee_number || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-7 w-7 rounded-full bg-[#1A1A1A] border border-[#262626] grid place-items-center text-xs font-medium">{(e.display_name || e.legal_name || "?").charAt(0).toUpperCase()}</div>
                    <div>
                      <div className="font-medium">{e.display_name || e.legal_name}</div>
                      <div className="text-xs font-mono text-[#71717A]">{e.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">{e.role_title}</td>
                <td className="px-4 py-3 text-[#A1A1AA]">{e.team || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{e.joining_date || "—"}</td>
                <td className="px-4 py-3 text-right font-mono text-xs">{e.tenure_days || 0}d</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 justify-end">
                    <div className="h-1 w-16 bg-[#1A1A1A] rounded"><div className="h-1 bg-[#5B9BFF] rounded" style={{ width: `${e.training_pct}%` }} /></div>
                    <span className="font-mono text-xs text-[#A1A1AA]">{e.training_pct}%</span>
                  </div>
                </td>
                <td className="px-4 py-3 pl-4">
                  {e.status === "pre_joining" ? <span className="tt-badge-warn">onboarding</span>
                    : e.status === "exited" ? <span className="tt-badge-neutral">exited</span>
                    : e.ready_at ? <span className="tt-badge-pass">ready</span>
                    : <span className="tt-badge-info">active</span>}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(ev) => ev.stopPropagation()}>
                  <button onClick={() => setReviewing({ employee_id: e.employee_id, display_name: e.display_name || e.legal_name, period: new Date().toISOString().slice(0, 7), rating: 4, visible_to_employee: true })} className="tt-btn-ghost text-[11px] text-[#A1A1AA] hover:text-[#F4F4F5] mr-2" data-testid={`review-btn-${e.employee_id}`}>Review</button>
                  {e.status !== "exited" && <button onClick={() => setExiting({ employee_id: e.employee_id, display_name: e.display_name || e.legal_name, exit_date: new Date().toISOString().slice(0,10), reason: "resigned", notes: "" })} className="tt-btn-ghost text-[11px] text-[#A1A1AA] hover:text-[#EF4444]" data-testid={`exit-btn-${e.employee_id}`}>End</button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-[#71717A]">No employees match.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="mt-8">
        <div className="tt-label mb-3">Teams</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {teams.map((t) => (
            <div key={t.team_id} className="tt-card p-4">
              <div className="font-medium">{t.name}</div>
              <div className="text-xs font-mono text-[#71717A]">{t.member_ids.length} members · lead: {employees.find((e) => e.employee_id === t.lead_employee_id)?.display_name || "—"}</div>
            </div>
          ))}
        </div>
      </div>

      {exiting && (
        <div className="fixed inset-0 bg-[rgba(10,10,10,0.85)] flex items-center justify-center z-40 p-4" onClick={() => setExiting(null)}>
          <div className="w-full max-w-md tt-card p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-1">End engagement</h2>
            <div className="text-sm text-[#A1A1AA] mb-4">{exiting.display_name}</div>
            <div className="tt-label mb-1">Last working day</div>
            <input type="date" className="tt-input mb-3 font-mono" value={exiting.exit_date} onChange={(e) => setExiting({ ...exiting, exit_date: e.target.value })} data-testid="exit-date" />
            <div className="tt-label mb-1">Reason</div>
            <select className="tt-input mb-3" value={exiting.reason} onChange={(e) => setExiting({ ...exiting, reason: e.target.value })} data-testid="exit-reason">
              <option value="resigned">Resigned</option>
              <option value="fired">Fired · for cause</option>
              <option value="contract_ended">Contract ended</option>
              <option value="other">Other</option>
            </select>
            <textarea rows={3} className="tt-input mb-4 text-sm" placeholder="Internal notes (not visible to employee)" value={exiting.notes} onChange={(e) => setExiting({ ...exiting, notes: e.target.value })} />
            <div className="text-xs text-[#71717A] mb-4">An offboarding checklist will be auto-created (revoke access, collect laptop, F&F, experience letter, handover).</div>
            <div className="flex justify-end gap-2"><button onClick={() => setExiting(null)} className="tt-btn-secondary text-sm">Cancel</button><button onClick={endEngagement} className="tt-btn-primary text-sm !bg-[#EF4444] hover:!bg-[#DC2626]" data-testid="exit-confirm">End engagement</button></div>
          </div>
        </div>
      )}

      {reviewing && (
        <div className="fixed inset-0 bg-[rgba(10,10,10,0.85)] flex items-center justify-center z-40 p-4" onClick={() => setReviewing(null)}>
          <div className="w-full max-w-md tt-card p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-1">Monthly review</h2>
            <div className="text-sm text-[#A1A1AA] mb-4">{reviewing.display_name}</div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div><div className="tt-label mb-1">Period</div><input type="month" className="tt-input font-mono" value={reviewing.period} onChange={(e) => setReviewing({ ...reviewing, period: e.target.value })} data-testid="review-period" /></div>
              <div>
                <div className="tt-label mb-1">Rating</div>
                <div className="flex gap-1 pt-1">
                  {[1,2,3,4,5].map((n) => <button key={n} onClick={() => setReviewing({ ...reviewing, rating: n })} className={`h-8 w-8 rounded grid place-items-center ${(reviewing.rating || 0) >= n ? "text-[#F5A524] bg-[#1A1A1A]" : "text-[#3F3F46] hover:text-[#71717A]"}`} data-testid={`review-star-${n}`}>★</button>)}
                </div>
              </div>
            </div>
            <textarea rows={2} className="tt-input mb-2 text-sm" placeholder="Strengths" value={reviewing.strengths || ""} onChange={(e) => setReviewing({ ...reviewing, strengths: e.target.value })} />
            <textarea rows={2} className="tt-input mb-2 text-sm" placeholder="Areas to improve" value={reviewing.improvements || ""} onChange={(e) => setReviewing({ ...reviewing, improvements: e.target.value })} />
            <textarea rows={2} className="tt-input mb-3 text-sm" placeholder="Goals for next month" value={reviewing.goals_next || ""} onChange={(e) => setReviewing({ ...reviewing, goals_next: e.target.value })} />
            <label className="flex items-center gap-2 text-xs mb-4"><input type="checkbox" className="accent-[#5B9BFF]" checked={!!reviewing.visible_to_employee} onChange={(e) => setReviewing({ ...reviewing, visible_to_employee: e.target.checked })} /> Visible to employee on their portal</label>
            <div className="flex justify-end gap-2"><button onClick={() => setReviewing(null)} className="tt-btn-secondary text-sm">Cancel</button><button onClick={saveReview} className="tt-btn-primary text-sm" data-testid="review-save">Save review</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
